import { useFieldArray, useForm } from "react-hook-form";
import { EMRLabTestsType } from "../../CustomerProfile/EMRCreation/Sections/LabTestSection";
import { AnimatePresence, motion } from "motion/react";
import EMRSectionSearch from "@/app/components/ui/Reusable/EMRSectionSearch";
import {
  EMRdetailsProps,
  IEMRSectionSearchResponseData,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import Input from "@/app/components/ui/Inputs/Input";
import { debounce, toNumber } from "lodash";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import EMRItemsSuggestionsComponent from "../EMRItemsSuggestionsComponent";
import DiagnosticAddOnsComponent, {
  IDiagnosticAddOn,
} from "../DiagnosticAddOnsComponent";
import EMRDeleteReasonModal from "../EMRDeleteReasonModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import toast from "react-hot-toast";
import { LOCKED_CHILD_ITEM_IDS } from "@/constants";

export interface IEMRLabTestSectionFormFields {
  lab_test_and_diagnostics: EMRLabTestsType[];
  recommended_tests: EMRLabTestsType[];
}

const isLockedChildLabItem = (itemId?: string | null, isChild?: boolean) =>
  Boolean(isChild && itemId && LOCKED_CHILD_ITEM_IDS.includes(itemId));

interface LabTestDiagnosticsComponentProps extends EMRdetailsProps {
  isVideoConsultation?: boolean;
  recommendedTestsOnly?: boolean;
}

const LabTestDiagnosticsComponent = ({
  emrDetails,
  setEmrDetails,
  isVideoConsultation,
  recommendedTestsOnly = false,
}: LabTestDiagnosticsComponentProps) => {
  const hideLabTests = isVideoConsultation || recommendedTestsOnly;
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();

  const [animateLast, setAnimateLast] = useState(false);
  const [animateLastRecomemded, setAnimateLastRecomemded] = useState(false);
  const [invalidLabQuantities, setInvalidLabQuantities] = useState<Record<number, boolean>>({});
  const [invalidRecommendedQuantities, setInvalidRecommendedQuantities] = useState<Record<number, boolean>>({});
  const [pendingDelete, setPendingDelete] = useState<{
    fieldName: "lab_test_and_diagnostics" | "recommended_tests";
    item: EMRLabTestsType;
  } | null>(null);
  const { execute: logAPI } = useClickLogAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const customerId = emrDetails?.customer_details?.customer_id;
  const pet_id = emrDetails?.pet_details?.pet_id;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: EMR_TEMPLATE_DELETE_LOCKED_ITEM — legacy role: super_admin
  const canDeleteLockedItem = useFeatureAccess("EMR_TEMPLATE_DELETE_LOCKED_ITEM");

  const formHook = useForm<IEMRLabTestSectionFormFields>({
    mode: "onChange",
  });

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const labTestAndDiagnosticsFormHook = useFieldArray<IEMRLabTestSectionFormFields>({
    name: "lab_test_and_diagnostics",
    control: formHook.control,
  });

  const recommendedTestsFormHook = useFieldArray<IEMRLabTestSectionFormFields>({
    name: "recommended_tests",
    control: formHook.control,
  });

  // True while the user has local edits that are not yet persisted. Set by
  // scheduleAutoSave/watch, cleared when the debounced save actually runs or
  // when a direct API call sends the current form values anyway.
  const pendingSaveRef = useRef(false);

  const debouncedUpdate = useMemo(
    () =>
      debounce((data) => {
        pendingSaveRef.current = false;
        const payload: IEMRUpdatePayloadProps = {
          lab_test_and_diagnostics: {
            lab_test_added: data.lab_test_and_diagnostics,
            lab_test_recommended: data.recommended_tests,
          },
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        if (JSON.stringify(data) === JSON.stringify(emrDetails?.lab_test_and_diagnostics)) return;
        updateEmrAPI(emr_id, payload);
      }, 2000),
    [emr_id, emrDetails?.emr_status],
  );

  // Quantity steppers/inputs mutate the form via setValue, which RHF reports
  // with type undefined — the watch filter below ignores those. Every
  // user-driven programmatic mutation must call this to persist.
  const scheduleAutoSave = () => {
    pendingSaveRef.current = true;
    debouncedUpdate(formHook.getValues());
  };

  const isFirstLoad = useRef(true);

  useEffect(() => {
    // Only auto-save on ACTUAL user input. RHF's watch callback signature is
    // (value, { name, type }) — `type` is defined ('change', 'blur', etc.) for
    // user events and undefined for programmatic setValue/reset/replace. Any
    // isFirstLoad timing dance is fragile because RHF doesn't guarantee that
    // the subscription callback fires synchronously with replace(); by the
    // time it fires, our setTimeout(0) has already flipped the flag back on.
    // Filtering on `type === "change"` is the deterministic fix.
    const subscription = formHook.watch((value, { type }) => {
      if (type !== "change") return;
      pendingSaveRef.current = true;
      debouncedUpdate(value as IEMRLabTestSectionFormFields);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  const handleAddNormalTest = async (item: IEMRSectionSearchResponseData, source?: string) => {
    const existing = formHook.getValues("lab_test_and_diagnostics");
    const isPresent = labTestAndDiagnosticsFormHook.fields.filter((f) => f.name === item.name);
    if (isPresent.length > 0) {
      toast("This Test already added.");
      return;
    }
    // This payload is built from getValues(), so any pending debounced edits
    // are already included — drop the pending save so the response replace runs.
    // Cancel only AFTER the duplicate early-return above, otherwise a rejected
    // duplicate add would silently discard the user's pending edits.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    const newItem = {
      name: item.name,
      remark: "",
      quantity: 1,
      lab_test_id: item?.lab_test_id ?? item?.vetic_variant_id,
    };
    setAnimateLast(true);
    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      lab_test_and_diagnostics: {
        lab_test_added: addedList,
        lab_test_recommended: formHook.getValues("recommended_tests") ?? [],
      },
      emr_status: emrDetails?.emr_status ?? emr_status,
      remove_children: emrDetails?.remove_children,
    };
    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    source && callLogSuggestionAPI(item);
  };

  const handleAddRecommendedTest = async (item: IEMRSectionSearchResponseData) => {
    const existing = formHook.getValues("recommended_tests");
    const isPresent = recommendedTestsFormHook.fields.filter((f) => f.name === item.name);
    if (isPresent.length > 0) {
      toast("Recommended Test already added.");
      return;
    }
    // Cancel only after the duplicate early-return; see handleAddNormalTest.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    setAnimateLastRecomemded(true);
    const newItem = {
      name: item.name,
      remark: "",
      quantity: 1,
      lab_test_id: item?.lab_test_id ?? item?.vetic_variant_id,
    };

    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      lab_test_and_diagnostics: {
        lab_test_added: formHook.getValues("lab_test_and_diagnostics") ?? [],
        lab_test_recommended: addedList,
      },
      emr_status: emrDetails?.emr_status ?? emr_status,
      remove_children: emrDetails?.remove_children,
    };
    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
  };

  useEffect(() => {
    if (emrDetails) {
      // The user has unsaved local edits (e.g. a quantity change) and another
      // section's save just refreshed emrDetails with data that predates them.
      // Persist the local edits NOW and keep the form as-is — replacing it
      // here would silently reset quantities back to the stale server values.
      if (pendingSaveRef.current) {
        debouncedUpdate.flush();
        return;
      }

      isFirstLoad.current = false; // disable auto-save
      // Cancel any pending debounced update. Without this, a debounced call
      // that was scheduled from the user's own typing will still fire AFTER
      // the reset below, pushing stale form values back to the server.
      debouncedUpdate.cancel();

      const labTestItems = emrDetails?.lab_test_and_diagnostics?.lab_test_added || [];
      labTestAndDiagnosticsFormHook.replace(
        labTestItems.map((item) => {
          if (isLockedChildLabItem(item?.lab_test_id, item?.is_child) && toNumber(item?.quantity) <= 0) {
            return {
              ...item,
              quantity: 1,
            };
          }
          return item;
        }),
      );

      const recommendedTestItems = emrDetails?.lab_test_and_diagnostics?.lab_test_recommended || [];
      recommendedTestsFormHook.replace(
        recommendedTestItems.map((item) => {
          if (isLockedChildLabItem(item?.lab_test_id, item?.is_child) && toNumber(item?.quantity) <= 0) {
            return {
              ...item,
              quantity: 1,
            };
          }
          return item;
        }),
      );

      // enable watch only after state settled
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  const isChilderRemovefromEMR = (index: number, item: any, reason?: string) => {
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    // Capture the remaining rows BEFORE remove() — RHF updates form values
    // synchronously, so filtering by index after removal drops a second item.
    const existing = (formHook.getValues("lab_test_and_diagnostics") ?? []).filter((_, i) => i !== index);
    labTestAndDiagnosticsFormHook.remove(index);
    let remove_children = null;
    if (item?.is_child) {
      remove_children = [...(emrDetails?.remove_children ?? []), item?.lab_test_id];
    } else {
      remove_children = [...(emrDetails?.remove_children ?? [])];
    }

    const payload: IEMRUpdatePayloadProps = {
      lab_test_and_diagnostics: {
        lab_test_added: existing,
        lab_test_recommended: formHook.getValues("recommended_tests") ?? [],
      },
      remove_children: remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    callLogAPILabDeleted(item?.lab_test_id, reason);
  };

  const removeRecommendedTest = (index: number, item: any, reason?: string) => {
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    const remaining = (formHook.getValues("recommended_tests") ?? []).filter((_, i) => i !== index);
    recommendedTestsFormHook.remove(index);
    const payload: IEMRUpdatePayloadProps = {
      lab_test_and_diagnostics: {
        lab_test_added: formHook.getValues("lab_test_and_diagnostics") ?? [],
        lab_test_recommended: remaining,
      },
      emr_status: emrDetails?.emr_status ?? emr_status,
    };
    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    callLogAPIRecommendedDeleted(item?.lab_test_id, reason);
  };

  const handleConfirmTaggedDelete = (reason: string) => {
    if (!pendingDelete) return;
    const { fieldName, item } = pendingDelete;
    const index = (formHook.getValues(fieldName) ?? []).findIndex((row) =>
      item?.lab_test_id ? row?.lab_test_id === item.lab_test_id : row?.name === item?.name,
    );
    if (index !== -1) {
      if (fieldName === "recommended_tests") {
        removeRecommendedTest(index, item, reason);
      } else {
        isChilderRemovefromEMR(index, item, reason);
      }
    }
    setPendingDelete(null);
  };

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLast(false), 600);
    return () => clearTimeout(t);
  }, [animateLast, emrDetails]);

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLastRecomemded(false), 600);
    return () => clearTimeout(t);
  }, [animateLastRecomemded, emrDetails]);

  const callLogAPIBase = (
    item: string,
    eventType: "EMR_QUANTITY_PLUS" | "EMR_QUANTITY_MINUS" | "EMR_ITEM_DELETED" | "SUGGESTION_ITEMS",
    fieldName: "lab_test_and_diagnostics" | "recommended_tests",
    reason?: string,
  ) => {
    const fieldValue = formHook.watch(fieldName);

    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: pet_id,
      click_on_search_item: item || undefined,
      [fieldName]: fieldValue || undefined,
      event_action: reason,
      clinic_id: mappedClinicId,
      emr_search_type: "Lab",
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  // Usage
  const callLogAPILab = (item: string) => callLogAPIBase(item, "EMR_QUANTITY_PLUS", "lab_test_and_diagnostics");
  const callLogSuggestionAPI = (item: any) => callLogAPIBase(item, "SUGGESTION_ITEMS", "lab_test_and_diagnostics");

  const callLogAPILabMinus = (item: string) => callLogAPIBase(item, "EMR_QUANTITY_MINUS", "lab_test_and_diagnostics");

  const callLogAPIRecommended = (item: string) => callLogAPIBase(item, "EMR_QUANTITY_PLUS", "recommended_tests");

  const callLogAPIRecommendedMinus = (item: string) => callLogAPIBase(item, "EMR_QUANTITY_MINUS", "recommended_tests");

  const callLogAPILabDeleted = (item: string, reason?: string) =>
    callLogAPIBase(item, "EMR_ITEM_DELETED", "lab_test_and_diagnostics", reason);

  const callLogAPIRecommendedDeleted = (item: string, reason?: string) =>
    callLogAPIBase(item, "EMR_ITEM_DELETED", "recommended_tests", reason);

  const labTestSuggestions =
    emrDetails?.lab_test_and_diagnostics?.lab_test_suggestions ?? [];
  const diagnosticAddOns = labTestSuggestions.filter(
    (item: IDiagnosticAddOn) => Boolean(item?.lab_test_id),
  );
  const regularLabTestSuggestions = labTestSuggestions.filter(
    (item: IDiagnosticAddOn) => !item?.lab_test_id,
  );
  const addedLabTests = formHook.watch("lab_test_and_diagnostics") ?? [];

  const handleDeleteDiagnosticAddon = (labId: string) => {
    const remainingSuggestions = labTestSuggestions.filter(
      (item: IDiagnosticAddOn) => item?.lab_test_id !== labId,
    );
    const payload: IEMRUpdatePayloadProps = {
      deleted_diagnostic_addon_id: labId,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };
    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(
          message || {
            ...emrDetails,
            lab_test_and_diagnostics: {
              ...emrDetails?.lab_test_and_diagnostics,
              lab_test_suggestions: remainingSuggestions,
            },
          },
        );
      }
    });
  };

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      {!recommendedTestsOnly && (
        <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Add Item</h3>
      )}

      {!hideLabTests && (
        <div className="bg-[#F9FAFB] rounded-xl p-2  ">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-10 sm:items-center pb-2">
            <h3 className="text-[#000000] text-sm font-medium tracking-tight42 w-full sm:w-2/12 shrink-0">Lab Test & Diagnostics</h3>
          <div className="w-full min-w-0 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddNormalTest(item);
              }}
              onFlexibleAdd={(itemName) => {
                if (itemName !== "") {
                  labTestAndDiagnosticsFormHook.append({
                    name: itemName,
                    remark: "",
                    quantity: 1,
                    lab_test_id: null,
                  });
                  scheduleAutoSave();
                }
              }}
              containerClassName="w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              placeholder="Search & Add Lab Test"
              searchType="Lab"
            />
          </div>
        </div>
        <div
          className={
            labTestAndDiagnosticsFormHook?.fields?.length > 0 ? "bg-white p-[10px] rounded-lg" : ""
          }
        >
          {labTestAndDiagnosticsFormHook?.fields?.length > 0 && (
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] rounded-md font-normal ">
                  <tr>
                  <th className="p-3 pl-2 w-1/2 font-medium text-left text-[10px] sm:text-xs">Item Names</th>
                  <th className="p-3 pl-2 w-1/4 font-medium text-center text-[10px] sm:text-xs">Quantity</th>
                  <th className="font-medium  w-1/4 text-center text-[10px] sm:text-xs ">Remarks</th>
                  <th className="font-medium w-1/4 text-center text-[10px] sm:text-xs pr-2">Actions</th>
                </tr>
                </thead>
                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {labTestAndDiagnosticsFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === labTestAndDiagnosticsFormHook?.fields?.length - 1;
                      const isSingle = labTestAndDiagnosticsFormHook?.fields?.length === 1;
                      const isLockedChildItem = isLockedChildLabItem(field?.lab_test_id, field?.is_child);
                      const isDeleteDisabled = isLockedChildItem && !canDeleteLockedItem;
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.lab_test_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp = animateLast && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <motion.tr
                          key={rowKey}
                          initial={initialProp}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.97 }}
                          // transition={{ duration: 0.28 }}
                          transition={{ duration: 0.45, ease: "easeInOut" }}
                          className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                        >
                          <td
                            className={`p-2 text-left ${
                              isSingle
                                ? "rounded-tl-xl rounded-bl-xl"
                                : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-b-xl" : ""}`.trim()
                            }`}
                          >
                            <input hidden {...formHook.register(`lab_test_and_diagnostics.${index}.name`)} />
                            <span className="text-[10px] sm:text-xs text-[#1F1F23] break-words font-normal">
                              {formHook.watch(`lab_test_and_diagnostics.${index}.name`)}
                            </span>
                            {field?.text_tag && (
                              <div>
                                <span className="inline-flex items-center rounded px-2 py-0.5 mt-1 text-[10px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20">
                                  {field.text_tag}
                                </span>
                              </div>
                            )}
                            {field?.is_child && (
                              <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                Suggested
                              </p>
                            )}
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="w-32 mx-auto">
                              <InputWithStartAndEndAddOn
                                startAddon="-"
                                startAddOnCallback={() => {
                                  const currentQuantity = toNumber(
                                    formHook.watch(`lab_test_and_diagnostics.${index}.quantity`),
                                  );
                                  if (isLockedChildItem && currentQuantity <= 1) {
                                    formHook.setValue(`lab_test_and_diagnostics.${index}.quantity`, 1);
                                  } else if (currentQuantity !== 1) {
                                    formHook.setValue(
                                      `lab_test_and_diagnostics.${index}.quantity`,
                                      currentQuantity - 1,
                                    );
                                  }
                                  scheduleAutoSave();
                                  callLogAPILabMinus(
                                    formHook.getValues(`lab_test_and_diagnostics.${index}.lab_test_id`),
                                  );
                                }}
                                step={1}
                                endAddOnCallback={() => {
                                  formHook.setValue(
                                    `lab_test_and_diagnostics.${index}.quantity`,
                                    formHook.watch(`lab_test_and_diagnostics.${index}.quantity`) + 1,
                                  );
                                  scheduleAutoSave();
                                  callLogAPILab(formHook.getValues(`lab_test_and_diagnostics.${index}.lab_test_id`));
                                }}
                                min={1}
                                endAddon="+"
                                data-emr-quantity="true"
                                data-invalid-quantity={invalidLabQuantities[index] ? "true" : "false"}
                                value={formHook.watch(`lab_test_and_diagnostics.${index}.quantity`)}
                                inputClassName={invalidLabQuantities[index] ? "!ring-red-500 !text-red-500" : ""}
                                onChange={(e: any) => {
                                  const rawValue = e.target.value;
                                  if (rawValue === "") {
                                    formHook.setValue(`lab_test_and_diagnostics.${index}.quantity`, "" as any);
                                    setInvalidLabQuantities((prev) => ({ ...prev, [index]: false }));
                                    return;
                                  }
                                  const parsedQuantity = toNumber(rawValue);
                                  if (!Number.isNaN(parsedQuantity)) {
                                    const safeQuantity = isLockedChildItem && parsedQuantity <= 0 ? 1 : parsedQuantity;
                                    formHook.setValue(
                                      `lab_test_and_diagnostics.${index}.quantity`,
                                      safeQuantity as any,
                                    );
                                    setInvalidLabQuantities((prev) => ({
                                      ...prev,
                                      [index]: safeQuantity < 0 || !Number.isInteger(safeQuantity),
                                    }));
                                    if (safeQuantity > 0 && Number.isInteger(safeQuantity)) {
                                      scheduleAutoSave();
                                    }
                                  }
                                  callLogAPILab(formHook.getValues(`lab_test_and_diagnostics.${index}.lab_test_id`));
                                }}
                                onBlur={() => {
                                  const currentValue: any = formHook.getValues(
                                    `lab_test_and_diagnostics.${index}.quantity`,
                                  );
                                  const parsedQuantity = toNumber(currentValue);

                                  if (currentValue === "" || Number.isNaN(parsedQuantity)) {
                                    formHook.setValue(`lab_test_and_diagnostics.${index}.quantity`, 1);
                                    setInvalidLabQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    return;
                                  }

                                  if (parsedQuantity <= 0) {
                                    formHook.setValue(`lab_test_and_diagnostics.${index}.quantity`, 1);
                                    setInvalidLabQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    if (!isLockedChildItem) {
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                    }
                                    return;
                                  }

                                  if (!Number.isInteger(parsedQuantity)) {
                                    setInvalidLabQuantities((prev) => ({ ...prev, [index]: true }));
                                    toast.error("Decimal, Zero and Negative values are not allowed !");
                                    return;
                                  }

                                  setInvalidLabQuantities((prev) => ({ ...prev, [index]: false }));
                                }}
                                type="number"
                              />
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                fieldError={formHook.formState.errors.lab_test_and_diagnostics}
                                register={formHook.register(`lab_test_and_diagnostics.${index}.remark`, {
                                  required: false,
                                })}
                                inputClassName="text-xs h-9"
                                type="text"
                                placeholder="Remarks"
                              />
                            </div>
                          </td>

                          <td
                            className={`p-2 text-center ${
                              isSingle
                                ? "rounded-tr-xl rounded-br-xl"
                                : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                            }`}
                          >
                            <button
                              className={`w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ${
                                isDeleteDisabled ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              disabled={isDeleteDisabled}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDeleteDisabled) {
                                  return;
                                }
                                if (field?.text_tag) {
                                  setPendingDelete({ fieldName: "lab_test_and_diagnostics", item: field });
                                  return;
                                }
                                isChilderRemovefromEMR(index, field);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="white"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-5 text-[#ED0101]"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                              </svg>
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
          <EMRItemsSuggestionsComponent
            handelCallback={(item, s) => handleAddNormalTest(item, s)}
            suggestionData={regularLabTestSuggestions}
          />
        </div>
        <DiagnosticAddOnsComponent
          diagnosticAddOns={diagnosticAddOns}
          onAddItem={(item, source) => handleAddNormalTest(item, source)}
          petName={emrDetails?.pet_details?.pet_name}
          addedLabTests={addedLabTests}
          customerId={customerId}
          petId={pet_id}
          emrId={emr_id ?? undefined}
          onDeleteAddon={handleDeleteDiagnosticAddon}
        />
      </div>
      )}

      <div className={`bg-[#F9FAFB] rounded-xl p-2 ${!hideLabTests ? "mt-2" : ""} `}>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-10 sm:items-center pb-2">
          <h3 className="text-[#000000] text-sm font-medium tracking-tight42 w-full sm:w-2/12 shrink-0">Recommended Test</h3>
          <div className="w-full min-w-0 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddRecommendedTest(item);
              }}
              onFlexibleAdd={(itemName) => {
                if (itemName !== "") {
                  recommendedTestsFormHook.append({
                    name: itemName,
                    remark: "",
                    quantity: 1,
                    lab_test_id: null,
                  });
                  scheduleAutoSave();
                }
              }}
              containerClassName="w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              placeholder="Search & Add Lab Test"
              searchType="Lab"
            />
          </div>
        </div>
        {recommendedTestsFormHook?.fields?.length > 0 && (
          <div className="bg-white p-[10px] rounded-lg ">
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] rounded-md font-normal ">
                  <tr>
                  <th className="p-3 pl-2 w-1/2 font-medium text-left text-[10px] sm:text-xs">Item Names</th>
                  <th className="p-3 pl-2 w-1/4 font-medium text-center text-[10px] sm:text-xs">Quantity</th>
                  <th className="font-medium  w-1/4 text-center text-[10px] sm:text-xs ">Remarks</th>
                  <th className="font-medium w-1/4 text-center text-[10px] sm:text-xs pr-2">Actions</th>
                </tr>
                </thead>
                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {recommendedTestsFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === recommendedTestsFormHook?.fields?.length - 1;
                      const isSingle = recommendedTestsFormHook?.fields?.length === 1;
                      const isLockedChildItem = isLockedChildLabItem(field?.lab_test_id, field?.is_child);
                      const isDeleteDisabled = isLockedChildItem && !canDeleteLockedItem;
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.lab_test_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp =
                        animateLastRecomemded && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <motion.tr
                          key={rowKey}
                          initial={initialProp}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.97 }}
                          // transition={{ duration: 0.28 }}
                          transition={{ duration: 0.45, ease: "easeInOut" }}
                          className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                        >
                          <td
                            className={`p-2 text-left ${
                              isSingle
                                ? "rounded-tl-xl rounded-bl-xl"
                                : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-b-xl" : ""}`.trim()
                            }`}
                          >
                            <input hidden {...formHook.register(`recommended_tests.${index}.name`)} />
                            <span className="text-[10px] sm:text-xs text-[#1F1F23] break-words font-normal">
                              {formHook.watch(`recommended_tests.${index}.name`)}
                            </span>
                            {field?.is_child && (
                              <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                Suggested
                              </p>
                            )}
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="w-32 mx-auto">
                              <InputWithStartAndEndAddOn
                                startAddon="-"
                                startAddOnCallback={() => {
                                  const currentQuantity = toNumber(formHook.watch(`recommended_tests.${index}.quantity`));
                                  if (isLockedChildItem && currentQuantity <= 1) {
                                    formHook.setValue(`recommended_tests.${index}.quantity`, 1);
                                  } else if (currentQuantity !== 1) {
                                    formHook.setValue(
                                      `recommended_tests.${index}.quantity`,
                                      currentQuantity - 1,
                                    );
                                  }
                                  scheduleAutoSave();
                                  callLogAPIRecommendedMinus(
                                    formHook.getValues(`recommended_tests.${index}.lab_test_id`),
                                  );
                                }}
                                step={1}
                                endAddOnCallback={() => {
                                  formHook.setValue(
                                    `recommended_tests.${index}.quantity`,
                                    formHook.watch(`recommended_tests.${index}.quantity`) + 1,
                                  );
                                  scheduleAutoSave();
                                  callLogAPIRecommended(formHook.getValues(`recommended_tests.${index}.lab_test_id`));
                                }}
                                min={1}
                                endAddon="+"
                                data-emr-quantity="true"
                                data-invalid-quantity={invalidRecommendedQuantities[index] ? "true" : "false"}
                                value={formHook.watch(`recommended_tests.${index}.quantity`)}
                                inputClassName={invalidRecommendedQuantities[index] ? "!ring-red-500 !text-red-500" : ""}
                                onChange={(e: any) => {
                                  const rawValue = e.target.value;
                                  if (rawValue === "") {
                                    formHook.setValue(`recommended_tests.${index}.quantity`, "" as any);
                                    setInvalidRecommendedQuantities((prev) => ({ ...prev, [index]: false }));
                                    return;
                                  }
                                  const parsedQuantity = toNumber(rawValue);
                                  if (!Number.isNaN(parsedQuantity)) {
                                    const safeQuantity = isLockedChildItem && parsedQuantity <= 0 ? 1 : parsedQuantity;
                                    formHook.setValue(`recommended_tests.${index}.quantity`, safeQuantity as any);
                                    setInvalidRecommendedQuantities((prev) => ({
                                      ...prev,
                                      [index]: safeQuantity < 0 || !Number.isInteger(safeQuantity),
                                    }));
                                    if (safeQuantity > 0 && Number.isInteger(safeQuantity)) {
                                      scheduleAutoSave();
                                    }
                                  }
                                  callLogAPIRecommended(formHook.getValues(`recommended_tests.${index}.lab_test_id`));
                                }}
                                onBlur={() => {
                                  const currentValue: any = formHook.getValues(`recommended_tests.${index}.quantity`);
                                  const parsedQuantity = toNumber(currentValue);

                                  if (currentValue === "" || Number.isNaN(parsedQuantity)) {
                                    formHook.setValue(`recommended_tests.${index}.quantity`, 1);
                                    setInvalidRecommendedQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    return;
                                  }

                                  if (parsedQuantity <= 0) {
                                    formHook.setValue(`recommended_tests.${index}.quantity`, 1);
                                    setInvalidRecommendedQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    if (!isLockedChildItem) {
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                    }
                                    return;
                                  }

                                  if (!Number.isInteger(parsedQuantity)) {
                                    setInvalidRecommendedQuantities((prev) => ({ ...prev, [index]: true }));
                                    toast.error("Decimal, Zero and Negative values are not allowed !");
                                    return;
                                  }

                                  setInvalidRecommendedQuantities((prev) => ({ ...prev, [index]: false }));
                                }}
                                type="number"
                              />
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                fieldError={formHook.formState.errors.recommended_tests}
                                register={formHook.register(`recommended_tests.${index}.remark`, {
                                  required: false,
                                })}
                                inputClassName="text-xs h-9"
                                type="text"
                                placeholder="Remarks"
                              />
                            </div>
                          </td>

                          <td
                            className={`p-2 text-center ${
                              isSingle
                                ? "rounded-tr-xl rounded-br-xl"
                                : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                            }`}
                          >
                            <button
                              className={`w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ${
                                isDeleteDisabled ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              disabled={isDeleteDisabled}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDeleteDisabled) {
                                  return;
                                }
                                if (field?.text_tag) {
                                  setPendingDelete({ fieldName: "recommended_tests", item: field });
                                  return;
                                }
                                removeRecommendedTest(index, field);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="white"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-5 text-[#ED0101]"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                              </svg>
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {pendingDelete && (
        <EMRDeleteReasonModal
          title={pendingDelete.fieldName === "recommended_tests" ? "Delete Recommended Test" : "Delete Lab Test"}
          itemName={pendingDelete.item?.name}
          variant="diagnostic"
          onConfirm={handleConfirmTaggedDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};
export default LabTestDiagnosticsComponent;
